import React, { useState } from "react";
import { renderUserAvatar } from "@/lib/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  SmilePlus, Pin, MoreHorizontal, Star, Reply,
  Trash2, Edit2, MessageSquare, Paperclip, ListTodo,
} from "lucide-react";
import PollCard from "@/components/channel/PollCard";

const EMOJIS = ["👍", "🎉", "❤️", "🔥", "🙌", "👀", "😂", "🚀"];

function timeStr(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

/**
 * A single channel message row — bubble on the right for the current user,
 * bubble on the left for everyone else. Handles inline edit, reactions,
 * thread openers, attachments, and the row-hover action menu.
 */
export default function MessageItem({
  msg, me, onReact, onThread, onDelete, onEdit, onPin, onSave, onToTask,
}) {
  const [editing, setEditing] = useState(false);
  const [ed, setEd] = useState(msg.text);
  const own = msg.user_id === me;
  return (
    <div
      className={`group flex gap-2 py-1.5 msg-enter ${own ? "flex-row-reverse" : ""}`}
      data-testid={`msg-${msg.id}`}
    >
      <img
        src={renderUserAvatar(msg.user)}
        className="w-8 h-8 rounded-full object-cover mt-0.5 shrink-0"
        alt=""
      />
      <div className={`flex flex-col max-w-[75%] min-w-0 ${own ? "items-end" : "items-start"}`}>
        <div className={`flex items-baseline gap-2 mb-0.5 px-1 ${own ? "flex-row-reverse" : ""}`}>
          {!own && (
            <span className="font-semibold text-[13px] text-zinc-800 truncate max-w-[160px]">
              {msg.user?.name || "Unknown"}
            </span>
          )}
          <span className="text-[10px] text-zinc-400 mono">{timeStr(msg.created_at)}</span>
          {msg.edited && <span className="text-[10px] text-zinc-400">(edited)</span>}
          {msg.pinned && <Pin className="w-3 h-3 text-[var(--brand-accent)]"/>}
        </div>
        {msg.poll && (
          <PollCard msg={msg} poll={msg.poll} currentUserId={me} own={own}/>
        )}
        {/*
          WhatsApp-style unified bubble: when a message carries both an image
          attachment AND a caption, we render one rounded container with the
          image on top and the caption text sitting inside the same bubble
          below. Uncaptioned images render as a bare rounded thumbnail.
          Documents keep the pill-chip style.
        */}
        {(() => {
          const atts = msg.attachments || [];
          if (atts.length === 0) return null;
          const first = atts[0];
          const isImage = (first.mime || "").startsWith("image/");
          const caption = (msg.text && !msg.text.startsWith("📎 ")) ? msg.text : "";
          const url = process.env.REACT_APP_BACKEND_URL + first.url;
          if (isImage) {
            return (
              <div
                className={`overflow-hidden rounded-2xl shadow-sm ${
                  own ? "bg-[var(--brand-accent)]" : "bg-zinc-100"
                }`}
                data-testid={`msg-att-bubble-${first.id || first.stored_as}`}
              >
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="block"
                  data-testid={`msg-att-image-${first.id || first.stored_as}`}
                >
                  <img
                    src={url}
                    alt={first.filename || ""}
                    className="w-full max-w-xs max-h-72 object-cover"
                  />
                </a>
                {caption && (
                  <div
                    className={`px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                      own ? "text-white" : "text-zinc-900"
                    }`}
                  >
                    {caption}
                  </div>
                )}
              </div>
            );
          }
          // Documents render as a chip (unchanged).
          return atts.map((a) => (
            <a
              key={a.id || a.stored_as || a.url}
              href={process.env.REACT_APP_BACKEND_URL + a.url}
              target="_blank"
              rel="noreferrer"
              className={`inline-flex items-center gap-2 text-xs rounded-full px-2.5 py-1 border ${
                own
                  ? "bg-white/15 text-white border-white/20 hover:bg-white/25"
                  : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
              }`}
              data-testid={`msg-att-doc-${a.id || a.stored_as}`}
            >
              <Paperclip className="w-3 h-3"/> {a.filename}
            </a>
          ));
        })()}
        {editing ? (
          <div className="mt-1 flex gap-2 w-full">
            <Input value={ed} onChange={(e) => setEd(e.target.value)} autoFocus />
            <Button size="sm" onClick={async () => { await onEdit(ed); setEditing(false); }}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        ) : (
          // Only render the standalone text bubble when there is NO attachment.
          // When there IS an attachment, the caption already lives inside the
          // unified image bubble above (see WhatsApp-style block).
          msg.text
          && !msg.text.startsWith("📎 ")
          && ((msg.attachments || []).length === 0
              || !((msg.attachments || [])[0].mime || "").startsWith("image/")) && (
            <div
              className={`${(msg.attachments || []).length > 0 ? "mt-1" : ""} rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words shadow-sm ${
                own
                  ? "bg-[var(--brand-accent)] text-white rounded-br-md"
                  : "bg-zinc-100 text-zinc-900 rounded-bl-md"
              }`}
            >
              {msg.text}
            </div>
          )
        )}
        {Object.keys(msg.reactions || {}).length > 0 && (
          <div className={`flex flex-wrap gap-1 mt-1 ${own ? "justify-end" : "justify-start"}`}>
            {Object.entries(msg.reactions).map(([e, uids]) => (
              <button
                key={e}
                onClick={() => onReact(msg, e)}
                className="text-xs px-1.5 py-0.5 rounded-full bg-white hover:bg-zinc-50 border border-zinc-200 shadow-sm"
              >
                {e} {uids.length}
              </button>
            ))}
          </div>
        )}
        {msg.reply_count > 0 && (
          <button
            onClick={() => onThread(msg)}
            className={`mt-1 text-xs text-[var(--brand-accent)] hover:underline flex items-center gap-1 ${own ? "self-end" : "self-start"}`}
          >
            <MessageSquare className="w-3 h-3"/> {msg.reply_count} {msg.reply_count === 1 ? "reply" : "replies"}
          </button>
        )}
      </div>
      <div className={`self-center opacity-0 group-hover:opacity-100 flex items-center gap-0.5 shrink-0 transition-opacity ${own ? "flex-row-reverse" : ""}`}>
        <Popover>
          <PopoverTrigger asChild>
            <button className="p-1.5 hover:bg-zinc-100 rounded" data-testid={`msg-react-${msg.id}`}>
              <SmilePlus className="w-4 h-4 text-zinc-500"/>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-1">
            <div className="flex gap-1">
              {EMOJIS.map((e) => (
                <button key={e} onClick={() => onReact(msg, e)} className="p-1 hover:bg-zinc-100 rounded">{e}</button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
        <button
          className="p-1.5 hover:bg-zinc-100 rounded"
          onClick={() => onThread(msg)}
          data-testid={`msg-thread-${msg.id}`}
        >
          <Reply className="w-4 h-4 text-zinc-500"/>
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-1.5 hover:bg-zinc-100 rounded"><MoreHorizontal className="w-4 h-4 text-zinc-500"/></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={() => onSave(msg)}><Star className="w-4 h-4 mr-2"/> Save</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onPin(msg)}><Pin className="w-4 h-4 mr-2"/> Pin</DropdownMenuItem>
            <DropdownMenuItem onClick={() => onToTask(msg)}><ListTodo className="w-4 h-4 mr-2"/> Convert to task</DropdownMenuItem>
            {own && <DropdownMenuItem onClick={() => setEditing(true)}><Edit2 className="w-4 h-4 mr-2"/> Edit</DropdownMenuItem>}
            {own && <DropdownMenuItem className="text-red-600" onClick={() => onDelete(msg)}><Trash2 className="w-4 h-4 mr-2"/> Delete</DropdownMenuItem>}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
